/**
 * Deposit reference and posted rates, 01.3 E1–E3 (`depositBeta.linearLag@1`). The pass-through β of a product is a constant, or (retail checking and retail money market, N3) a
 * logistic in fed funds: the market rate is the SimStart rate plus the integral of β over the move in fed funds from FF₀, so it depends on where fed funds are, never on the path
 * (E1). One speed h per product; h = 1 is lag-free (indexed MMDA, brokered); h falls to h × `downAsymmetry` while the target sits below the reference (E2, P4-D3: deposit rates
 * follow a cut more slowly than a hike). The decision offset sits outside the lag (I27), so a rate decision posts at once.
 */
import type { DepositBetaModel, DepositBetaParams, Rate } from "@bsim/domain";

/** B(x) = ∫β dx: β·x for a constant β, else β_min·x + (β_max − β_min) ÷ s · ln(1 + e^(s·(x − x_mid))) (softplus written for large |z|). Only differences of B are used. */
const betaIntegral = (p: DepositBetaParams, x: number): number => {
  const g = p.logistic;
  if (g === undefined) return p.betaThroughCycle * x;
  const z = g.slope * (x - g.midpointRate);
  return g.betaMin * x + ((g.betaMax - g.betaMin) / g.slope) * (Math.max(z, 0) + Math.log1p(Math.exp(-Math.abs(z))));
};

/** 01.3 E1: β(FF), the pass-through of one more point at fed funds `ffRate`; the constant β where the product has no `logistic`. */
export const betaAt = (p: DepositBetaParams, ffRate: number): number => {
  const g = p.logistic;
  return g === undefined ? p.betaThroughCycle : g.betaMin + (g.betaMax - g.betaMin) / (1 + Math.exp(-g.slope * (ffRate - g.midpointRate)));
};

/** 01.3 E1: the pass-through of a whole move of `shock` (decimal, signed, non-zero) from `ffRate`: the mean of β over it, [B(ff + shock) − B(ff)] ÷ shock. 05.5 E2 reads it. */
export const betaOverMove = (p: DepositBetaParams, ffRate: number, shock: number): number => (betaIntegral(p, ffRate + shock) - betaIntegral(p, ffRate)) / shock;

export const depositBetaLinearLag: DepositBetaModel = {
  kind: "depositBeta",
  nextRate: (call, input) => {
    const { adjustmentSpeedPerMonth, downAsymmetry = 1, floorRate } = call.ref.params;
    const target = input.startRate + betaIntegral(call.ref.params, call.env.ffRate) - betaIntegral(call.ref.params, input.ffAtStart); // 01.3 E1
    const h = target < input.referenceRate ? adjustmentSpeedPerMonth * downAsymmetry : adjustmentSpeedPerMonth;
    const referenceRate = input.referenceRate + h * (target - input.referenceRate); // 01.3 E2
    const postedRate = Math.max(floorRate, referenceRate + input.stanceOffsetBp / 10_000); // 01.3 E3
    return { referenceRate: referenceRate as Rate, postedRate: postedRate as Rate };
  },
};
